/* -------------------------------------------------------------------------------------------------
   TD Draft 2026-10-02 CART-UP2 — compact cart lines (cart drawer + /cart). Loaded as a module on every page by
   sections/cart-drawer.liquid (outside <cart-drawer>, so it survives the drawer refresh) and by
   sections/main-cart.liquid; it runs once per page. Settings come from the data attributes of those script tags.

   1. Focus after "Entfernen" (snippets/td-cart-line.liquid). The theme removes the line via /cart/change.js and then
      re-renders the drawer (the focused link disappears, focus would fall to <body>) or reloads /cart. Afterwards the
      focus goes to "Entfernen" of the line that now takes the removed line's place, else of the line before it, else
      to the drawer / page heading, and a polite status says "Artikel entfernt".
   2. The 2nd-cover code (section setting td_cu_discount_code, TDB8BNN7R since 03.10.2026; CART-UP3: BEZUG10 no longer
      exists) does not stay in the cart without its purpose:
      - without effect (e.g. the cover was removed): taken out right after the change, from the cart JSON of the
        theme's cart:change, or from /cart.js after a removal on /cart. Nothing on the page changes; the code just
        does not reach the checkout as an invalid code.
      - still deducting, but no single blanket left in the cart (e.g. the blanket was removed): Liquid renders a
        marker [data-td-cu-guard] (snippets/td-cart-cover-upsell.liquid); the code is taken out, then the drawer
        re-renders (theme event cart:refresh) or /cart reloads once, so the cover shows its normal price.
      Every other code (customer codes, the set code) stays exactly as it is. /cart/update.js replaces the cart's
      codes, so the full list minus ours is sent.
   TD Draft 2026-10-05 APP-DISC (Felix 05.10.2026: discount app SMART Functions Creator): td_cu_discount_code is empty,
   so part 2 never runs — data-cover-code is empty and the guard marker is not rendered; no code is read, sent or
   removed. The cover discount ("Extra −10%") comes from the app.
   3. TD 2026-10-27 SET-GUARD (order #48404TD: a blanket got "Set −10%" without its cover): the app deducts "Set −10%"
      from every line with the property _td_set ("<blanket36>-<partner36>", assets/td-set-builder.js). When one part of
      a set leaves the cart, the other line kept the property and the 10%. A set line whose partner (variant id from
      the key, same _td_set value) is no longer in the cart now loses the value: /cart/change.js with _td_set = null,
      the same quantity and its other properties (keepalive, so it also lands when the checkout follows at once). Only
      after Shopify's answer shows the line without the value, the drawer re-renders (cart:refresh) or /cart reloads,
      so the price shown is the price charged. Complete sets are never touched. Runs after every theme cart:change and
      on every page load with a set line (data-td-set, snippets/td-cart-line.liquid), so a cart left with such a line
      earlier is fixed before the drawer's checkout; carts without set lines send no extra request.
   ------------------------------------------------------------------------------------------------- */
const REMOVED_KEY = 'td_cl_removed';
const RELOAD_KEY = 'td_cl_guard_reload';
const DRAWER_MAX_AGE = 10000;
const PAGE_MAX_AGE = 60000;

const session = {
  get(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  },
  set(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      /* storage blocked: the focus after a removal on /cart and the reload limits are skipped */
    }
  },
  remove(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      /* ignore */
    }
  }
};

const routeRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const isCartPage = () => !!(window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === 'cart');
const sameCode = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

function config() {
  const scripts = Array.from(document.querySelectorAll('script[data-td-cart-lines]'));
  const wanted = isCartPage() ? 'page' : 'drawer';
  const own = scripts.find((script) => script.dataset.context === wanted) || scripts[0];
  return {
    coverCode: own ? (own.dataset.coverCode || '').trim() : '',
    removedText: own ? own.dataset.removedText || '' : ''
  };
}

/* ---------- 1. Focus after "Entfernen" ---------- */
let pending = null;

const linesIn = (root) => Array.from(root.querySelectorAll('line-item.td-cl'));

/* The theme announces every quantity change on the line before it sends it; quantity 0 = "Entfernen" */
document.addEventListener('line-item:will-change', (event) => {
  const line = event.target instanceof Element ? event.target.closest('line-item') : null;
  if (!line || !line.classList.contains('td-cl')) return;
  if (!event.detail || Number(event.detail.targetQuantity) !== 0) return;
  const drawer = line.closest('cart-drawer');
  const root = drawer || document.querySelector('main') || document.body;
  pending = { context: drawer ? 'drawer' : 'page', index: linesIn(root).indexOf(line), t: Date.now(), announced: false };
  if (!drawer) session.set(REMOVED_KEY, JSON.stringify(pending));
});

function focusLost(root) {
  const active = document.activeElement;
  return !active || active === document.body || active === document.documentElement || active === root || !active.isConnected;
}

function focusAfterRemoval(root, heading, info) {
  if (!focusLost(root)) return;
  const lines = linesIn(root);
  const line = lines[info.index] || lines[info.index - 1] || lines[lines.length - 1];
  let target = line ? line.querySelector('.td-cl__remove') || line.querySelector('.td-cl__title') : null;
  if (!target) target = heading;
  if (!target) return;
  if (!target.matches('a[href], button, input, select, textarea, [tabindex]')) target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
  if (typeof target.scrollIntoView === 'function') target.scrollIntoView({ block: 'nearest' });
}

/* A polite status inside the drawer / page (a live region outside the open drawer may not be read) */
function announce(root, text) {
  if (!text) return;
  let region = Array.from(root.children).find((child) => child.hasAttribute('data-td-cl-status'));
  if (!region) {
    region = document.createElement('p');
    region.className = 'sr-only';
    region.setAttribute('role', 'status');
    region.setAttribute('data-td-cl-status', '');
    root.appendChild(region);
  }
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = text;
  }, 150);
}

/* ---------- 2. The 2nd-cover code ---------- */
/* One code change at a time (each one sends the full list of codes) */
let queue = Promise.resolve();
const serial = (task) => {
  const run = queue.then(task, task);
  queue = run.catch(() => {});
  return run;
};
let guardRuns = [];

async function getCart() {
  try {
    const response = await fetch(`${routeRoot()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    const cart = await response.json();
    return cart && Array.isArray(cart.discount_codes) ? cart : null;
  } catch (error) {
    return null;
  }
}

/* Sets exactly these codes; the new cart or null */
async function setCodes(codes) {
  try {
    const response = await fetch(`${routeRoot()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ discount: codes.join(',') })
    });
    if (!response.ok) return null;
    const cart = await response.json();
    return cart && Array.isArray(cart.discount_codes) ? cart : null;
  } catch (error) {
    return null;
  }
}

/* Takes our code out of the given cart's codes; true when the returned cart no longer has it */
async function removeCode(cart, code) {
  const rest = cart.discount_codes.map((entry) => entry.code).filter((entry) => entry && !sameCode(entry, code));
  const after = await setCodes(rest);
  return !!after && !after.discount_codes.some((entry) => sameCode(entry.code, code));
}

const idleIn = (cart, code) => !!cart && Array.isArray(cart.discount_codes) && cart.discount_codes.some((entry) => sameCode(entry.code, code) && !entry.applicable);

/* Our code without effect: out of the cart, silently (prices and totals do not change). The cart of the change only
   says whether to act; the codes are read fresh right before the change. */
function dropIfIdle(cart) {
  const code = config().coverCode;
  if (!code || !idleIn(cart, code)) return Promise.resolve(false);
  return serial(async () => {
    const fresh = await getCart();
    return idleIn(fresh, code) ? removeCode(fresh, code) : false;
  });
}

/* Our code still deducting without a single blanket (marker from Liquid): out, then show the cart without it.
   Resolves true when /cart reloads. At most three runs in 30 s, and only after a verified removal, so it never loops. */
function runGuard() {
  const guard = document.querySelector('[data-td-cu-guard]');
  const code = guard ? (guard.getAttribute('data-code') || '').trim() : '';
  if (!code) return Promise.resolve(false);
  const now = Date.now();
  guardRuns = guardRuns.filter((time) => now - time < 30000);
  if (guardRuns.length >= 3) return Promise.resolve(false);
  guardRuns.push(now);

  return serial(async () => {
    const cart = await getCart();
    const removed = !!cart && cart.discount_codes.some((entry) => sameCode(entry.code, code)) && (await removeCode(cart, code));
    if (!removed) return false;
    if (isCartPage()) {
      const last = Number(session.get(RELOAD_KEY)) || 0;
      if (Date.now() - last < 20000) return false;
      session.set(RELOAD_KEY, String(Date.now()));
      window.location.reload();
      return true;
    }
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    return false;
  });
}

/* ---------- 3. Set guard ---------- */
const SET_RELOAD_KEY = 'td_cl_set_reload';
let setRuns = [];

const setIds = (key) => String(key || '').split('-').map((part) => parseInt(part, 36)).filter((id) => id > 0);

/* Lines with _td_set whose partner line (same value, variant id from the key) is missing */
function orphanSetLines(cart) {
  const items = cart && Array.isArray(cart.items) ? cart.items : [];
  return items.filter((item) => {
    const key = item.properties && item.properties._td_set;
    const ids = setIds(key);
    if (!key || ids.length < 2) return false;
    return !ids.every((id) => items.some((other) => other.variant_id === id && other.properties && other.properties._td_set === key));
  });
}

/* The line with _td_set = null (Shopify keeps a property that is just left out), same quantity and other properties.
   keepalive: the change still reaches Shopify when the page is left for the checkout. The new cart, or null. */
async function dropSetProperty(item) {
  const properties = Object.assign({}, item.properties, { _td_set: null });
  try {
    const response = await fetch(`${routeRoot()}cart/change.js`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: item.key, quantity: item.quantity, properties })
    });
    if (!response.ok) return null;
    const cart = await response.json();
    return cart && Array.isArray(cart.items) ? cart : null;
  } catch (error) {
    return null;
  }
}

/* /cart after a verified fix: reload, so the page shows the new prices. A verified fix cannot come back on the next
   load; more than three reloads in 20 s would still mean a loop, so then the page stays as it is. */
function reloadCartPage() {
  const now = Date.now();
  const recent = String(session.get(SET_RELOAD_KEY) || '').split(',').map(Number).filter((time) => now - time < 20000);
  if (recent.length >= 3) return false;
  session.set(SET_RELOAD_KEY, recent.concat(now).join(','));
  window.location.reload();
  return true;
}

/* The given cart only says whether to act; the lines are read fresh before the first change (line keys change with
   the properties), then each change answers with the new cart. Resolves true when /cart reloads. */
function runSetGuard(cart) {
  if (!orphanSetLines(cart).length) return Promise.resolve(false);
  const now = Date.now();
  setRuns = setRuns.filter((time) => now - time < 30000);
  if (setRuns.length >= 3) return Promise.resolve(false);
  setRuns.push(now);

  return serial(async () => {
    let current = await getCart();
    let fixed = false;
    for (let step = 0; step < 10; step += 1) {
      const orphan = orphanSetLines(current)[0];
      if (!orphan) break;
      const after = await dropSetProperty(orphan);
      /* refused, or the line still carries the value: stop instead of retrying */
      if (!after || orphanSetLines(after).some((line) => line.key === orphan.key)) break;
      current = after;
      fixed = true;
    }
    if (!fixed) return false;
    if (isCartPage()) return reloadCartPage();
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    return false;
  });
}

/* ---------- Wiring ---------- */
if (!window.__tdCartLines) {
  window.__tdCartLines = true;

  /* Drawer: removal handled by the theme (cart:change with the new cart), then its refresh */
  document.addEventListener('cart:change', (event) => {
    const cart = event.detail && event.detail.cart;
    dropIfIdle(cart);
    /* the set guard renders the drawer once more: focus and status after a removal wait for that render */
    if (pending && pending.context === 'drawer' && orphanSetLines(cart).length) pending.setNext = true;
    runSetGuard(cart);
  });

  document.addEventListener('cart-drawer:refreshed', (event) => {
    const drawer = event.target instanceof Element ? event.target.closest('cart-drawer') : null;
    if (drawer && pending && pending.context === 'drawer' && Date.now() - pending.t < DRAWER_MAX_AGE) {
      const info = pending;
      focusAfterRemoval(drawer, drawer.querySelector('[slot="header"]'), info);
      /* With the code guard in this render, or a set line the set guard is fixing, the drawer renders once more (and
         drops the focus and status again): the status waits for that render, at most 4 s. */
      const guardNext = !!drawer.querySelector('[data-td-cu-guard]') || !!info.setNext;
      info.setNext = false;
      const say = () => {
        if (info.announced) return;
        info.announced = true;
        window.clearTimeout(info.timer);
        announce(drawer, config().removedText);
      };
      if (!guardNext) say();
      else if (!info.timer) info.timer = window.setTimeout(say, 4000);
      /* kept only for the second render the code guard or the set guard is about to trigger */
      if (!guardNext) pending = null;
    }
    runGuard();
  });

  /* Page load: the marker first (it may reload /cart once); then, on any page with a set line, one without its
     partner; after a removal on /cart: focus, status, idle code */
  (async () => {
    if (await runGuard()) return;
    if (document.querySelector('line-item.td-cl[data-td-set]') && (await runSetGuard(await getCart()))) return;
    if (!isCartPage()) return;
    let info = null;
    try {
      info = JSON.parse(session.get(REMOVED_KEY) || 'null');
    } catch (error) {
      info = null;
    }
    session.remove(REMOVED_KEY);
    if (!info || info.context !== 'page' || Date.now() - (info.t || 0) > PAGE_MAX_AGE) return;
    const main = document.querySelector('main') || document.body;
    focusAfterRemoval(main, main.querySelector('h1'), info);
    announce(main, config().removedText);
    dropIfIdle(await getCart());
  })();
}
